import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Network, Loader2, X, ImagePlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  downscaleDiagramImage,
  requestNetworkDiagramExtraction,
  isAcceptedDiagramFile,
  getImageExtension,
} from '../../../../services/networkDiagramService';
import { showErrorToast } from '../../../../utils/toastUtils';

const STEPS = ['reading', 'extracting', 'mapping'];

/**
 * NetworkDiagramButton — compact entry card (1/4 width) for network diagrams
 * (png/jpg/jpeg). One AI vision pass extracts support assets + CVE findings +
 * techniques, reusing the scan review/pool pipeline. Separate option from scan reports.
 */
const NetworkDiagramButton = ({ analysisId, businessAssets, disabled, onBusyChange, onExtracted }) => {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [isWorking, setIsWorking] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [diagramFile, setDiagramFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    onBusyChange && onBusyChange(isWorking);
  }, [isWorking, onBusyChange]);

  useEffect(() => (
    () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }
  ), [previewUrl]);

  const errorMessage = (code) => {
    switch (code) {
      case 'too_big':
        return t('workshop4.networkDiagram.tooBig', 'Image trop volumineuse (max 8 Mo, réduite automatiquement si possible).');
      case 'bad_type':
        return t('workshop4.networkDiagram.badType', 'Formats acceptés : .png, .jpg, .jpeg.');
      default:
        return t('workshop4.networkDiagram.extractError', "L'analyse a échoué. Veuillez contacter ACG pour assistance.");
    }
  };

  const handleSelect = (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    if (!isAcceptedDiagramFile(file)) {
      showErrorToast(errorMessage(
        ['.png', '.jpg', '.jpeg', '.webp'].includes(getImageExtension(file.name)) ? 'too_big' : 'bad_type'
      ));
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setDiagramFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const clearSelection = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setDiagramFile(null);
    setPreviewUrl(null);
  };

  const handleAnalyze = async () => {
    if (!analysisId) {
      showErrorToast(t('workshop4.status.noAnalysisSelected'));
      return;
    }
    if (!diagramFile) {
      showErrorToast(t('workshop4.networkDiagram.noImage', 'Choisissez un schéma réseau.'));
      return;
    }
    setIsWorking(true);
    setStepIndex(0);
    try {
      const image = await downscaleDiagramImage(diagramFile);
      setStepIndex(1);
      const data = await requestNetworkDiagramExtraction({
        analysisId,
        filename: diagramFile.name,
        image,
        businessAssets,
      });
      setStepIndex(2);
      onExtracted && onExtracted({
        assets: data.assets || [],
        findings: data.findings || [],
        techniques: data.techniques || [],
        filename: diagramFile.name,
        source: diagramFile.name,
        origin: 'diagram',
        truncated: false,
        droppedTechniques: data.droppedTechniques || 0,
        droppedAssets: data.droppedAssets || 0,
        provider: data.provider,
        fallback: !!data.fallback,
      });
      clearSelection();
    } catch (err) {
      console.error('[NetworkDiagram] extraction failed:', err);
      showErrorToast(errorMessage(err?.message));
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-teal-200 p-4 h-full flex flex-col">
      <div className="flex items-start gap-2">
        <div className="bg-teal-100 p-2 rounded-lg shrink-0">
          <Network size={20} className="text-teal-700" />
        </div>
        <div className="min-w-0">
          <p className="font-semibold text-slate-800 text-sm">
            {t('workshop4.networkDiagram.title', "Analyse de l'architecture du système étudié")}
          </p>
        </div>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept=".png,.jpg,.jpeg,.webp"
        className="hidden"
        onChange={handleSelect}
        disabled={disabled || isWorking}
      />
      {previewUrl ? (
        <div className="relative mt-3">
          <img
            src={previewUrl}
            alt={diagramFile?.name || ''}
            className="w-full max-h-40 object-contain rounded-lg border border-slate-200 bg-slate-50"
          />
          <button
            onClick={clearSelection}
            disabled={isWorking}
            className="absolute top-1 right-1 p-1 rounded-full bg-white border border-slate-300 text-slate-500 hover:text-red-600 disabled:opacity-50"
            title={t('common.delete', 'Supprimer')}
          >
            <X size={14} />
          </button>
          <p className="text-xs text-slate-500 truncate mt-1">{diagramFile?.name}</p>
        </div>
      ) : (
        <button
          onClick={() => fileRef.current && fileRef.current.click()}
          disabled={disabled || isWorking || !analysisId}
          className="mt-3 flex-1 min-h-[7rem] border-2 border-dashed border-teal-200 rounded-lg text-slate-400 hover:border-teal-400 hover:text-teal-600 disabled:opacity-50 flex flex-col items-center justify-center gap-1.5 p-3 transition"
        >
          <ImagePlus size={24} />
          <span className="text-xs font-medium text-center">
            {t('workshop4.networkDiagram.placeholder', 'Cliquez pour choisir un schéma (png, jpg)')}
          </span>
        </button>
      )}

      <button
        onClick={handleAnalyze}
        disabled={disabled || isWorking || !analysisId || !diagramFile}
        className="mt-3 text-sm font-medium bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center shadow-sm transition duration-200"
      >
        {isWorking ? (
          <>
            <Loader2 size={16} className="mr-2 animate-spin" />
            {t(`workshop4.networkDiagram.steps.${STEPS[stepIndex]}`, 'Analyse…')}
          </>
        ) : (
          <>
            <Network size={16} className="mr-2" />
            {t('workshop4.networkDiagram.analyze', 'Analyser le schéma')}
          </>
        )}
      </button>
    </div>
  );
};

NetworkDiagramButton.propTypes = {
  analysisId: PropTypes.string,
  businessAssets: PropTypes.array,
  disabled: PropTypes.bool,
  onBusyChange: PropTypes.func,
  onExtracted: PropTypes.func,
};

NetworkDiagramButton.defaultProps = {
  businessAssets: [],
  disabled: false,
};

export default NetworkDiagramButton;
